/**
 * Service Worker Registrierung
 * Registriert und verwaltet den Service Worker
 */

export interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

/**
 * Registriert den Service Worker
 */
export function registerServiceWorker(config?: ServiceWorkerConfig) {
  // Prüfe ob Service Worker unterstützt wird
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker wird von diesem Browser nicht unterstützt');
    return;
  }

  // Registriere Service Worker nach dem Laden der Seite
  window.addEventListener('load', () => {
    const swUrl = '/wp-content/plugins/skyvision-konfigurator/dist/service-worker.js';

    navigator.serviceWorker
      .register(swUrl)
      .then(registration => {
        console.log('[Service Worker] Registriert:', registration);

        // Prüfe auf Updates
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          
          if (installingWorker == null) {
            return;
          }

          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // Neuer Service Worker verfügbar
                console.log('[Service Worker] Neue Version verfügbar');
                
                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                }
              } else {
                // Service Worker zum ersten Mal installiert
                console.log('[Service Worker] Bereit für Offline-Nutzung');
                
                if (config && config.onSuccess) {
                  config.onSuccess(registration);
                }
              }
            }
          };
        };
      })
      .catch(error => {
        console.error('[Service Worker] Registrierung fehlgeschlagen:', error);
      });
  });

  // Online/Offline Events
  window.addEventListener('online', () => {
    console.log('[Service Worker] Online');
    if (config && config.onOnline) {
      config.onOnline();
    }
  });

  window.addEventListener('offline', () => {
    console.log('[Service Worker] Offline');
    if (config && config.onOffline) {
      config.onOffline();
    }
  });
}

/**
 * Deregistriert den Service Worker
 */
export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error('[Service Worker] Deregistrierung fehlgeschlagen:', error);
      });
  }
}

/**
 * Löscht alle Caches
 */
export async function clearServiceWorkerCache(): Promise<boolean> {
  if ('serviceWorker' in navigator && 'caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('[Service Worker] Cache gelöscht');
      return true;
    } catch (error) {
      console.error('[Service Worker] Cache löschen fehlgeschlagen:', error);
      return false;
    }
  }
  return false;
}

/**
 * Prüft ob die App offline ist
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Zeigt eine Benachrichtigung wenn eine neue Version verfügbar ist
 */
export function showUpdateNotification(registration: ServiceWorkerRegistration) {
  const updateMessage = 'Eine neue Version ist verfügbar. Seite neu laden?';
  
  if (confirm(updateMessage)) {
    // Aktiviere den wartenden Service Worker
    const waitingWorker = registration.waiting;
    
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      
      // Warte auf Controller-Wechsel und lade dann neu
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  }
}
